import { z } from "zod";
import {
  INFERENCE_GEOGRAPHIES,
  MODEL_ROLES,
  modelImplicationsSchema,
  RETENTION_MODES,
  residencyViewSchema,
} from "./models.ts";

/**
 * The backoffice Models screen (model-policy-and-transparency): the catalogue with each model's
 * implications, whether the instance offers it, the tenant's consent, and the model policy at
 * one level. Catalogue keys and display names only, never inference profile IDs.
 */

/**
 * A model's consent state for the tenant: `not_required` (default retention), `none` (never
 * accepted), `accepted` (the current implications version), `outdated` (an earlier version;
 * unavailable until accepted again) or `revoked`.
 */
export const MODEL_CONSENT_STATES = [
  "not_required",
  "none",
  "accepted",
  "outdated",
  "revoked",
] as const;
export const modelConsentStateSchema = z.enum(MODEL_CONSENT_STATES);
export type ModelConsentState = z.infer<typeof modelConsentStateSchema>;

export const adminModelSchema = z.object({
  key: z.string(),
  displayName: z.string(),
  implications: modelImplicationsSchema,
  implicationsVersion: z.int(),
  consentRequired: z.boolean(),
  /**
   * Whether this instance offers the model at all: its retention mode, a granted geography and a
   * role that routes it there.
   */
  offered: z.boolean(),
  /** Why the instance does not offer it; null when offered. */
  unavailableReason: z.enum(["retention", "geography", "not_routed"]).nullable(),
  consent: z.object({
    state: modelConsentStateSchema,
    /** The version accepted (current or earlier); null when never accepted. */
    implicationsVersion: z.int().nullable(),
    acceptedAt: z.iso.datetime().nullable(),
    acceptedByName: z.string().nullable(),
    revokedAt: z.iso.datetime().nullable(),
  }),
});
export type AdminModel = z.infer<typeof adminModelSchema>;

/** A model policy setting as resolved at the viewed level. */
const policySettingSchema = <T extends z.ZodType>(value: T) =>
  z.object({
    value,
    source: z.enum(["instance", "tenant", "entity", "project"]),
    sourceEntityId: z.string().nullable(),
    /** True when a level above locks it. */
    locked: z.boolean(),
    /** True when this level's own value is locked for the levels below. */
    lockedHere: z.boolean(),
  });

export const adminModelsSchema = z.object({
  catalogueVersion: z.string(),
  /**
   * The most permissive retention mode a connection declares. Kept one release for the live
   * SPA; read `connections` instead.
   */
  retentionMode: z.enum(RETENTION_MODES),
  /** The instance's model connections and the retention mode each declares (ADR 0017). */
  connections: z.array(z.object({ id: z.string(), retention: z.enum(RETENTION_MODES) })).optional(),
  geographies: z.array(z.enum(INFERENCE_GEOGRAPHIES)),
  /** The level the policy is shown for: the tenant (null) or an entity. */
  entityId: z.string().nullable(),
  /** Tenant admins accept and revoke consent; entity admins read it. */
  canManageConsent: z.boolean(),
  models: z.array(adminModelSchema),
  policy: z.object({
    residency: residencyViewSchema,
    allowed: policySettingSchema(z.array(z.enum(["chat", "reasoning", "fast"]))).extend({
      /** What the level above allows. */
      inherited: z.array(z.enum(["chat", "reasoning", "fast"])),
    }),
    allowedModels: policySettingSchema(z.array(z.string())).extend({
      /** What the level above allows: this level can only keep models from it. */
      inherited: z.array(z.string()),
      /**
       * The resolved list as stored (family IDs and model keys, model-picking 5.10b); optional
       * for one release.
       */
      stored: z.array(z.string()).optional(),
      /** It names only versions the catalogue keeps as older: the admin should re-pick. */
      olderOnly: z.boolean().optional(),
    }),
    advancedPicker: policySettingSchema(z.boolean()).extend({
      /** Off above means off here. */
      inherited: z.boolean(),
    }),
  }),
  /**
   * Per role: whether the level needs it, the models usable under residency, availability and
   * consent (`candidates`, what the Advanced editor may keep), and those `allowedModels` keeps.
   */
  roles: z.array(
    z.object({
      role: z.enum(MODEL_ROLES),
      needed: z.boolean(),
      candidates: z.array(z.string()),
      usable: z.array(z.string()),
    }),
  ),
});
export type AdminModels = z.infer<typeof adminModelsSchema>;

export const modelConsentInputSchema = z.strictObject({
  /** The implications version the admin read; `409 implications_changed` when not current. */
  implicationsVersion: z.int().min(1),
});
export type ModelConsentInput = z.infer<typeof modelConsentInputSchema>;

/**
 * The `data-model` part of an assistant message (spec `trust-indicators` "Model line on
 * answers"): the catalogue models that served its steps, in order, and whether a fallback
 * answered: `unavailable`, a health failover (a candidate other than the first allowed one);
 * `policy`, the user's effort or model choice was not allowed (model-effort-picker D3), which
 * wins over a failover in the same answer. Stored with the message; the client takes display
 * names from the catalogue.
 */
export const MODEL_PART = "data-model";
export const MODEL_PART_ID = "model";
export const MODEL_FALLBACKS = ["none", "unavailable", "policy"] as const;
export type ModelFallback = (typeof MODEL_FALLBACKS)[number];
/**
 * Why the answer's model was the one it was (model-picking D11): `first`, the effort's (or the
 * picked family's) resolved version; `older_version`, an older version of the same family
 * because the newest was unavailable; `neighbour_family`, the same lab's neighbour family;
 * `pin_unhonoured`, the version the organisation pinned was unavailable; `unavailable`, another
 * failover; `policy`, the user's choice was not allowed (it wins over the others);
 * `provider_withdrawn`, the conversation's provider pick is no longer offered.
 */
export const MODEL_REASONS = [
  "first",
  "older_version",
  "neighbour_family",
  "pin_unhonoured",
  "unavailable",
  "policy",
  "provider_withdrawn",
] as const;
export type ModelReason = (typeof MODEL_REASONS)[number];
export const modelPartDataSchema = z.object({
  models: z.array(z.string()),
  /** Kept one release beside `reason` (the live SPA reads it). */
  fallback: z.enum(MODEL_FALLBACKS),
  /** Absent on older answers: read `fallback`. */
  reason: z.enum(MODEL_REASONS).optional(),
  catalogueVersion: z.string(),
  /**
   * The answer's resolved role (`chat`, `reasoning`, `fast`), never a model ID: what a feedback
   * draft from "Report a problem" names (feedback-from-conversation D4). Absent on older answers.
   */
  role: z.string().max(20).optional(),
});
export type ModelPartData = z.infer<typeof modelPartDataSchema>;
